import {useMemo} from "react";
import {useMembersTranslation} from '@/i18n/hooks';
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import ServiceSelector from "@/components/service-selector";
import {Text} from "@/components/tailwind/text";
import {Switch, SwitchField} from "@/components/tailwind/switch";
import {Label, Description} from "@/components/tailwind/fieldset";
import {Briefcase, Star} from "lucide-react";

type ServiceAssignmentPanelProps = {
    services: ServiceWithGroupId[];
    groups: ServiceGroup[];
    selectedServiceIds: string[];
    onServiceChange: (ids: string[]) => void;
    isDriver: boolean;
    onDriverChange: (value: boolean) => void;
    isLoading?: boolean;
};

function ServiceAssignmentPanel({
    services,
    groups,
    selectedServiceIds,
    onServiceChange,
    isDriver,
    onDriverChange,
    isLoading = false,
}: ServiceAssignmentPanelProps) {
    const {t} = useMembersTranslation();

    const regularServices = useMemo(() =>
        services.filter(s => !s.isSpecialAssignment),
        [services]
    );

    const specialAssignmentServices = useMemo(() =>
        services.filter(s => s.isSpecialAssignment),
        [services]
    );

    const selectedRegularServiceIds = useMemo(() =>
        selectedServiceIds.filter(id => regularServices.some(s => s.id === id)),
        [selectedServiceIds, regularServices]
    );

    const selectedSpecialAssignmentIds = useMemo(() =>
        selectedServiceIds.filter(id => specialAssignmentServices.some(s => s.id === id)),
        [selectedServiceIds, specialAssignmentServices]
    );

    function handleRegularServicesChange(ids: string[]) {
        onServiceChange([...ids, ...selectedSpecialAssignmentIds]);
    }

    function handleSpecialAssignmentsChange(ids: string[]) {
        onServiceChange([...selectedRegularServiceIds, ...ids]);
    }

    return (
        <>
            {/* Driver Section */}
            <div className="mb-6">
                <SwitchField>
                    <Label>{t('memberEditDialog.services.driver.label')}</Label>
                    <Description>{t('memberEditDialog.services.driver.description')}</Description>
                    <Switch
                        checked={isDriver}
                        onChange={onDriverChange}
                        color="blue"
                        data-testid="driver-switch"
                    />
                </SwitchField>
            </div>

            {/* Regular Services Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                    <label
                        className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100"
                        id="services-label"
                    >
                        <Text>{t('memberEditDialog.services.servicesLabel')}</Text>
                    </label>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <Text className="text-gray-500">
                            {t('memberEditDialog.services.loading')}
                        </Text>
                    </div>
                ) : (
                    <ServiceSelector
                        services={regularServices}
                        groups={groups}
                        selectedServiceIds={selectedRegularServiceIds}
                        onChange={handleRegularServicesChange}
                        placeholder={t('memberEditDialog.services.selectServices')}
                        aria-labelledby="services-label"
                        aria-required="false"
                        data-testid="regular-services-selector"
                    />
                )}
            </div>

            {/* Special Assignments Section */}
            {specialAssignmentServices.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Star className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                        <label
                            className="block text-sm/6 font-medium text-gray-900 dark:text-gray-100"
                            id="special-assignments-label"
                        >
                            <Text>{t('memberEditDialog.services.specialAssignmentsLabel')}</Text>
                        </label>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-4">
                            <Text className="text-gray-500">
                                {t('memberEditDialog.services.loading')}
                            </Text>
                        </div>
                    ) : (
                        <ServiceSelector
                            services={specialAssignmentServices}
                            groups={groups}
                            selectedServiceIds={selectedSpecialAssignmentIds}
                            onChange={handleSpecialAssignmentsChange}
                            placeholder={t('memberEditDialog.services.selectSpecialAssignments')}
                            aria-labelledby="special-assignments-label"
                            aria-required="false"
                            data-testid="special-assignments-selector"
                        />
                    )}
                </div>
            )}
        </>
    );
}

export default ServiceAssignmentPanel;
